import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
} from "@heroicons/react/outline";

import AccountingWrapper from "../../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../../Components/v2/Accounting/AccountingPageHeader";
import FormDate from "../../../../../Components/v2/Form/FormDate";
import { getAging, useAuthState } from "../../../../../Context";
import { EXPORTS } from "../../../../../Constants/accountingReport";
import {
  formatPeriodDate,
  toWibPickerDate,
  wibEndOfDay,
  wibStartOfDay,
  wibToday,
} from "../../../../../Utils/PeriodUtils";
import { Money, formatRupiah } from "../VatRecap/sections";
import {
  EmptyState,
  LoadError,
  ReportTypeTabs,
  TD,
  TD_NUM,
  TH,
  TH_NUM,
  useReportExport,
} from "./shared";
import {
  OVERDUE_KEYS,
  RECEIVABLE,
  documentPath,
  fiscalYearStart,
  hasLedgerDifference,
  ledgerAccountsToName,
  summarizeAging,
  typeFromParam,
} from "./utils";

const BADGE_NEUTRAL =
  "inline-flex items-center whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/20";

const Summary = ({ aging, type }) => {
  const { t } = useTranslation();
  const summary = summarizeAging(aging);
  const isReceivable = type === RECEIVABLE;
  return (
    <section
      aria-label={t("reports.aging.summary")}
      className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm"
    >
      <dl className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <div>
          <dt className="text-sm text-gray-600">
            {t(`reports.aging.total.${type}`)}
          </dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {formatRupiah(summary.total)}
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 sm:text-right">
          <div>
            <dt className="text-sm text-gray-600">
              {t("reports.aging.overdue")}
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
              {formatRupiah(summary.overdue)}{" "}
              <span className="text-sm font-normal text-gray-600">
                ({summary.overduePercent}%)
              </span>
            </dd>
          </div>
          {isReceivable && (
            <div>
              <dt className="text-sm text-gray-600">
                {t("reports.aging.deposit")}
              </dt>
              <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {formatRupiah(summary.deposit)}
              </dd>
              <dd className="text-xs text-gray-600">
                {t("reports.aging.depositHint")}
              </dd>
            </div>
          )}
        </div>
      </dl>
      {/* Decorative; the legend below carries every label and amount. */}
      <div
        aria-hidden="true"
        className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-gray-100"
      >
        {summary.buckets
          .filter((bucket) => bucket.value > 0)
          .map((bucket) => (
            <span
              key={bucket.key}
              className={bucket.color}
              style={{ width: `${bucket.percent}%` }}
            />
          ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
        {summary.buckets.map((bucket) => (
          <li key={bucket.key}>
            <span
              aria-hidden="true"
              className={`mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-middle ${bucket.color}`}
            />
            <span className="text-gray-600">{bucket.label}</span>
            <br />
            <span
              className={`font-medium tabular-nums ${bucket.value ? "text-gray-900" : "text-gray-500"}`}
            >
              <Money value={bucket.value} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const DifferenceNotice = ({ aging, type }) => {
  const { t } = useTranslation();
  const accounts = ledgerAccountsToName(aging);
  const names = accounts
    .map((account) => `${account.code} ${account.name}`)
    .join(", ");
  if (!hasLedgerDifference(aging)) {
    return (
      <p className="text-sm text-gray-600">
        {t("reports.aging.matches", {
          accounts: names,
          amount: formatRupiah(aging?.ledgerBalance),
          interpolation: { escapeValue: false },
        })}
      </p>
    );
  }
  return (
    <div
      role="status"
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <p className="font-medium">
        {t("reports.aging.difference.title", {
          amount: formatRupiah(Math.abs(aging.difference)),
        })}
      </p>
      <p className="mt-1">
        {t(`reports.aging.difference.body.${type}`, {
          total: formatRupiah(aging.total),
          accounts: names,
          balance: formatRupiah(aging.ledgerBalance),
          interpolation: { escapeValue: false },
        })}{" "}
        {accounts.map((account) => (
          <Link
            key={account.id}
            to={`/v2/accounting/transactions?accountId=${encodeURIComponent(account.id)}`}
            className="font-medium underline"
          >
            {t("reports.aging.difference.link", { code: account.code })}
          </Link>
        ))}
      </p>
    </div>
  );
};

const PartnerRows = ({ row, type, columns, ledgerLink }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = `aging-docs-${row.partnerID || "none"}`;
  const name = row.partnerID ? row.partnerName : t("reports.aging.noPartner");
  return (
    <Fragment>
      <tr>
        <td className="whitespace-nowrap px-3 py-2 text-sm">
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              aria-label={t(
                open
                  ? "reports.aging.hideDocuments"
                  : "reports.aging.showDocuments",
                { name },
              )}
              onClick={() => setOpen((value) => !value)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {open ? (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            {row.partnerID ? (
              <Link
                to={ledgerLink(row.partnerID)}
                className="text-blue-600 hover:underline"
              >
                {name}
              </Link>
            ) : (
              <span className="text-gray-900">{name}</span>
            )}
          </span>
        </td>
        {columns.map((key) => (
          <td
            key={key}
            className={`${TD_NUM} ${row.totals[key] ? "text-gray-900" : "text-gray-500"}`}
          >
            <Money value={row.totals[key]} />
          </td>
        ))}
        <td className={`${TD_NUM} font-medium text-gray-900`}>
          <Money value={row.total} />
        </td>
        {type === RECEIVABLE && (
          <td
            className={`${TD_NUM} ${row.deposit ? "text-gray-900" : "text-gray-500"}`}
          >
            <Money value={row.deposit} />
          </td>
        )}
      </tr>
      {open && (
        <tr id={panelId} className="bg-gray-50">
          <td
            colSpan={columns.length + (type === RECEIVABLE ? 3 : 2)}
            className="py-2 pl-12 pr-3"
          >
            {row.documents.length === 0 ? (
              <p className="text-sm text-gray-600">
                {t("reports.aging.noDocuments")}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-600">
                    <th className="py-1 text-left font-medium">
                      {t("reports.aging.columns.number")}
                    </th>
                    <th className="text-left font-medium">
                      {t("reports.aging.columns.date")}
                    </th>
                    <th className="text-left font-medium">
                      {t("reports.aging.columns.dueDate")}
                    </th>
                    <th className="text-right font-medium">
                      {t("reports.aging.columns.total")}
                    </th>
                    <th className="text-right font-medium">
                      {t("reports.aging.columns.balance")}
                    </th>
                    <th className="text-right font-medium">
                      {t("reports.aging.columns.overdue")}
                    </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums text-gray-900">
                  {row.documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="py-1">
                        <Link
                          to={documentPath(type, doc.id)}
                          className="text-blue-600 hover:underline"
                        >
                          {doc.number || "-"}
                        </Link>
                      </td>
                      <td>{formatPeriodDate(doc.date, i18n.language)}</td>
                      <td>{formatPeriodDate(doc.dueDate, i18n.language)}</td>
                      <td className="text-right">
                        <Money value={doc.total} />
                      </td>
                      <td className="text-right">
                        <Money value={doc.balance} />
                      </td>
                      <td className="text-right">
                        {doc.daysOverdue > 0 ? (
                          t("reports.aging.daysLate", {
                            count: doc.daysOverdue,
                          })
                        ) : (
                          <span className={BADGE_NEUTRAL}>
                            {t("reports.aging.notDue")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );
};

const AgingTable = ({ aging, type, ledgerLink }) => {
  const { t } = useTranslation();
  const buckets = aging.buckets || [];
  const overdue = buckets.filter((bucket) => OVERDUE_KEYS.includes(bucket.key));
  const columns = buckets.map((bucket) => bucket.key);
  const isReceivable = type === RECEIVABLE;
  return (
    <section aria-labelledby="aging-detail-heading">
      <h3
        id="aging-detail-heading"
        className="mb-2 text-base font-semibold text-gray-900"
      >
        {t("reports.aging.detail")}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th rowSpan={2} scope="col" className={`${TH} align-bottom`}>
                {t("reports.aging.columns.partner")}
              </th>
              <th rowSpan={2} scope="col" className={`${TH_NUM} align-bottom`}>
                {buckets.find((bucket) => bucket.key === "current")?.label}
              </th>
              <th
                colSpan={overdue.length}
                scope="colgroup"
                className="border-b border-gray-200 px-3 pb-1 pt-2 text-center text-sm font-medium text-gray-900"
              >
                {t("reports.aging.overdueDays")}
              </th>
              <th rowSpan={2} scope="col" className={`${TH_NUM} align-bottom`}>
                {t("reports.aging.columns.total")}
              </th>
              {isReceivable && (
                <th
                  rowSpan={2}
                  scope="col"
                  className={`${TH_NUM} align-bottom`}
                >
                  {t("reports.aging.columns.deposit")}
                </th>
              )}
            </tr>
            <tr>
              {overdue.map((bucket) => (
                <th
                  key={bucket.key}
                  scope="col"
                  className="px-3 pb-2 text-right text-sm font-medium text-gray-900"
                >
                  {t(`reports.aging.bucketsShort.${bucket.key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {aging.items.map((row) => (
              <PartnerRows
                key={row.partnerID || "none"}
                row={row}
                type={type}
                columns={columns}
                ledgerLink={ledgerLink}
              />
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold text-gray-900">
            <tr>
              <td className={TD}>{t("reports.aging.columns.total")}</td>
              {columns.map((key) => (
                <td key={key} className={TD_NUM}>
                  <Money value={aging.totals[key]} />
                </td>
              ))}
              <td className={TD_NUM}>
                <Money value={aging.total} />
              </td>
              {isReceivable && (
                <td className={TD_NUM}>
                  <Money value={aging.deposit} />
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

const AccountingAging = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthState();
  const business = user?.mainBusiness;
  const businessID = business?.id;

  // Type and date live in the URL so a link or a refresh opens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const type = typeFromParam(searchParams.get("type"));
  const dateParam = searchParams.get("date");
  const date = useMemo(
    () => (dateParam ? toWibPickerDate(dateParam) : wibToday()),
    [dateParam],
  );
  const endDate = wibEndOfDay(date);

  const [rawAging, setAging] = useState(null);
  // Bucket labels come from the API in Indonesian; show them in the UI language.
  const aging = useMemo(
    () =>
      rawAging && {
        ...rawAging,
        buckets: (rawAging.buckets || []).map((bucket) => ({
          ...bucket,
          label: t(`reports.aging.buckets.${bucket.key}`, {
            defaultValue: bucket.label,
          }),
        })),
      },
    [rawAging, t],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!businessID || !endDate) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAging({ businessID, type, date: endDate })
      .then((data) => {
        if (!cancelled) setAging(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setAging(null);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessID, type, endDate, attempt]);

  const { exportAs, exporting, errorModal } = useReportExport((exportType) =>
    getAging({ businessID, type, date: endDate, exportType }),
  );

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // The partner ledger opens on this fiscal year up to the aging date.
  const ledgerLink = (partnerID) =>
    `/v2/accounting/reports/partner-ledger?${new URLSearchParams({
      partnerId: partnerID,
      type,
      startDate: wibStartOfDay(
        fiscalYearStart(business?.fiscalYearStartMonth, date),
      ),
      endDate,
    }).toString()}`;

  const renderBody = () => {
    if (error && !loading) {
      return (
        <LoadError error={error} onRetry={() => setAttempt((n) => n + 1)} />
      );
    }
    if (loading || !aging) {
      return (
        <div
          className="h-40 animate-pulse rounded-lg border border-gray-200 bg-gray-50"
          aria-busy="true"
        />
      );
    }
    return (
      <>
        <Summary aging={aging} type={type} />
        {hasLedgerDifference(aging) && (
          <DifferenceNotice aging={aging} type={type} />
        )}
        {aging.items.length === 0 ? (
          <EmptyState
            title={t(`reports.aging.empty.${type}`, {
              date: formatPeriodDate(endDate, i18n.language),
            })}
          />
        ) : (
          <AgingTable aging={aging} type={type} ledgerLink={ledgerLink} />
        )}
        {!hasLedgerDifference(aging) && (
          <DifferenceNotice aging={aging} type={type} />
        )}
      </>
    );
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="reports.aging.name"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => exportAs("pdf"),
            items: EXPORTS.map((item) => ({
              name: t(item.label),
              onClick: () => exportAs(item.value),
            })),
            // The header shows a spinner whenever this is disabled.
            disabled: exporting,
          }}
        />
        <ReportTypeTabs
          value={type}
          onChange={(next) =>
            setParam("type", next === RECEIVABLE ? null : next)
          }
        />
        <div className="space-y-6 px-4 py-5 sm:px-6 lg:px-8">
          <div className="w-full sm:max-w-xs">
            <FormDate
              id="aging-date"
              label="reports.aging.date"
              selected={date}
              onChange={(value) =>
                value && setParam("date", wibEndOfDay(value))
              }
              dateFormat="dd/MM/yyyy"
            />
          </div>
          {renderBody()}
        </div>
        {errorModal}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingAging;
